import React, { FC } from "react"
import { useForm, UseFormRegisterReturn, FieldError } from 'react-hook-form';
import { useCookies } from "react-cookie"

import api from 'api';
import utils from 'utils';

import styles from '../Input/Input.module.scss';
import dropdownStyles from '../Dropdown/Dropdown.module.scss';
import './Form.scss';

import Button from "components/Button";
import { getReferral } from 'components/ReferralWrapper';


interface FormProps {
  text: string,
  firstNameLabel: string
  lastNameLabel: string
  jobcenterLabel: string
  jobcenterOptions: any[]
  jobcenterErrorMessage: string
  jobcenterTooltip: string
  emailLabel: string
  phoneLabel: string
  buttonLabel: string
  successMessage: string
  language: "de" | "en"
  stageId: number
  leadSource: string
}

type FormData = {
  firstName: string,
  lastName: string,
  phone: string,
  email: string,
  jobcenter: string
}

interface InputProps {
  disabled: boolean,
  label: string,
  error?: FieldError,
  register: UseFormRegisterReturn,
  type: string
}

interface DropdownProps {
  disabled: boolean,
  label: string,
  error?: FieldError,
  register: UseFormRegisterReturn,
  tooltip: string,
  options: { item: string }[]
}

const Input: FC<InputProps> = ({ label, error, register, type, disabled }) => {
  return (
    <label className={`${styles.label} ${error ? styles.error : ""}`}>
      {label}
      <input disabled={disabled} className={`${styles.input}`} type={type} {...register} />
    </label>
  )
}

const Dropdown: FC<DropdownProps> = ({ label, error, register, options, tooltip, disabled }) => {
  return (
    <>
      <label className={`${dropdownStyles.label} ${error ? dropdownStyles.error : ""}`}>
        <div className="jobcenter-dropdown-label">{label}
          <div className="jobcenter-info-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 1.5C6.20156 1.5 1.5 6.20156 1.5 12C1.5 17.7984 6.20156 22.5 12 22.5C17.7984 22.5 22.5 17.7984 22.5 12C22.5 6.20156 17.7984 1.5 12 1.5ZM12 20.7188C7.18594 20.7188 3.28125 16.8141 3.28125 12C3.28125 7.18594 7.18594 3.28125 12 3.28125C16.8141 3.28125 20.7188 7.18594 20.7188 12C20.7188 16.8141 16.8141 20.7188 12 20.7188Z" fill="white" />
              <path d="M14.6153 7.42192C13.9121 6.80552 12.984 6.46802 11.9996 6.46802C11.0153 6.46802 10.0871 6.80786 9.38401 7.42192C8.65276 8.06177 8.24963 8.92192 8.24963 9.84302V10.0211C8.24963 10.1243 8.33401 10.2086 8.43713 10.2086H9.56213C9.66526 10.2086 9.74963 10.1243 9.74963 10.0211V9.84302C9.74963 8.80942 10.7598 7.96802 11.9996 7.96802C13.2395 7.96802 14.2496 8.80942 14.2496 9.84302C14.2496 10.5719 13.734 11.2399 12.9348 11.5469C12.4379 11.7368 12.016 12.0696 11.7137 12.5055C11.4067 12.9508 11.2473 13.4852 11.2473 14.0266V14.5305C11.2473 14.6336 11.3317 14.718 11.4348 14.718H12.5598C12.6629 14.718 12.7473 14.6336 12.7473 14.5305V13.9985C12.7485 13.771 12.8183 13.5491 12.9474 13.3618C13.0766 13.1745 13.2593 13.0305 13.4715 12.9485C14.8543 12.4165 15.7473 11.1977 15.7473 9.84302C15.7496 8.92192 15.3465 8.06177 14.6153 7.42192ZM11.0621 17.1555C11.0621 17.4042 11.1609 17.6426 11.3367 17.8184C11.5125 17.9942 11.751 18.093 11.9996 18.093C12.2483 18.093 12.4867 17.9942 12.6625 17.8184C12.8384 17.6426 12.9371 17.4042 12.9371 17.1555C12.9371 16.9069 12.8384 16.6684 12.6625 16.4926C12.4867 16.3168 12.2483 16.218 11.9996 16.218C11.751 16.218 11.5125 16.3168 11.3367 16.4926C11.1609 16.6684 11.0621 16.9069 11.0621 17.1555Z" fill="white" />
            </svg>
            <div className="jobcenter-tooltip">
              <p>{tooltip}</p>
            </div>
          </div>
        </div>
        <select
          disabled={disabled}
          className={dropdownStyles.dropdown} {...register}>
          {options.map((option) => (
            <option value={option.item} key={option.item}>{option.item}</option>
          ))}
        </select>
      </label>
      {error && <div
        className="header-reskill__form__error"
        dangerouslySetInnerHTML={
          { __html: utils.cleanText(error.message, true) }
        }
      />}
    </>
  )
}

export const Form: FC<FormProps> = ({
  text,
  firstNameLabel,
  lastNameLabel,
  jobcenterLabel,
  jobcenterOptions,
  jobcenterErrorMessage,
  jobcenterTooltip,
  emailLabel,
  phoneLabel,
  buttonLabel,
  successMessage,
  language,
  stageId,
  leadSource
}) => {
  const [cookies] = useCookies()
  const { register, handleSubmit, formState: { errors, isSubmitting, isValid, isDirty, isSubmitSuccessful } } = useForm<FormData>({
    mode: "all",
    defaultValues: {
      firstName: "",
      lastName: "",
      jobcenter: "",
      phone: "",
      email: "",
    }
  });
  const onSubmit = async (data: FormData) => {
    (window as any).ttq.track('SubmitForm');
    const customFields = {
      // Lead Source ->
      '19ddc486147c0bed2f2d0ca5a540854ef33bdf69': `${leadSource}${getReferral(cookies.ref)}`,
      phone: data.phone,
      '8a2d9fc96a0b7d037839d71aa4b3acc774c9b9c8': data.jobcenter,
      'dc0a286b9fe00ab025597776b38364624d981bb9': language === 'de' ? 'Deutsch' : 'English',
    };
    const customData = {
      registered: data.jobcenter,
      language: language === "de" ? 'Deutsch' : 'English'
    };
    api.trackFbEvent({
      eventName: "Lead",
      externalId: cookies.session,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      customDataObj: customData
    })
    await api.addPerson(
      data.firstName,
      data.lastName,
      data.email,
      customFields,
      leadSource,
      stageId
    )
  }

  const i18n = {
    jobcenterTooltip: {
      en: "You are currently (or will soon be) unemployed & are officially registered in Germany (not with a student visa).",
      de: "Du bist im Moment oder demnächst arbeitslos & offiziell in Deutschland gemeldet (kein Student)."
    }
  }

  return (
    <form className="landing-page__hero__form slider slider-delay-1 reset-form-styles" onSubmit={handleSubmit(onSubmit)}>
      <img
        className="landing-page__hero__form__floater"
        src={require('assets/floaters/stairs.png')}
        alt=""
      />
      <img
        className="landing-page__hero__form__floater"
        src={require('assets/floaters/cube.png')}
        alt=""
      />
      <img
        className="landing-page__hero__form__floater"
        src={require('assets/floaters/stairs-white4.png')}
        alt=""
      />
      <div className="landing-page__hero__form__text">
        {text}
      </div>

      <div className="row">
        <div className="col-6 col-12-sm">
          <Input disabled={isSubmitting || isSubmitSuccessful} type={"text"} label={`${firstNameLabel}*`} error={errors.firstName} register={register("firstName", { required: true })} />
        </div>
        <div className="col-6 col-12-sm">
          <Input disabled={isSubmitting || isSubmitSuccessful} type={"text"} label={`${lastNameLabel}*`} error={errors.lastName} register={register("lastName", { required: true })} />
        </div>
      </div>
      <Dropdown
        disabled={isSubmitting || isSubmitSuccessful}
        label={`${jobcenterLabel}*`}
        error={errors.jobcenter}
        tooltip={i18n.jobcenterTooltip[language]}
        register={register(
          "jobcenter",
          {
            required: {
              value: true,
              message: jobcenterErrorMessage
            },
            validate: (value) => (value === "Yes" || value === "Ja") || jobcenterErrorMessage
          })
        }
        options={jobcenterOptions}
      />
      <Input disabled={isSubmitting || isSubmitSuccessful} type={"text"} label={`${emailLabel}*`} error={errors.email} register={register("email", { required: true, pattern: /^\S+@\S+$/i })} />
      <Input disabled={isSubmitting || isSubmitSuccessful} type={"tel"} label={`${phoneLabel}*`} error={errors.phone} register={register("phone", { required: true, minLength: 7 })} />
      <div className="landing-page__hero__form__button">
        {isSubmitSuccessful ?
          <div className="landing-page__hero__form__message">
            {successMessage}
          </div>
          :
          <Button
            busy={isSubmitting}
            disabled={isSubmitting || !isDirty || !isValid || isSubmitSuccessful}
            onClick={() => undefined}
            label={`${buttonLabel}`}
            variant="transparent-blue-alt"
          />}
      </div>
    </form>
  )
}